/**
 * Variant Summary Component
 *
 * Compact display of variant information for inventory items
 */

import {
  CubeIcon,
  CurrencyDollarIcon,
  PackageIcon,
  TagIcon,
} from '@phosphor-icons/react';
import { useMemo } from 'react';

import { Badge } from '@/components/ui/badge';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Progress } from '@/components/ui/progress';
import { InventoryItem } from '@/types/inventory';
import { formatCurrency, formatNumber } from '@/utils';

interface VariantSummaryProps {
  inventoryItem: InventoryItem;
  showDetails?: boolean;
  className?: string;
}

export const VariantSummary: React.FC<VariantSummaryProps> = ({
  inventoryItem,
  showDetails = true,
  className = '',
}) => {
  const variants = inventoryItem.variant || [];

  const summary = useMemo(() => {
    if (!variants.length) {
      return {
        totalVariants: 0,
        totalStock: inventoryItem.quantity,
        totalValue: inventoryItem.quantity * (inventoryItem.price || 0),
        lowStockCount:
          inventoryItem.quantity <= inventoryItem.reorderLevel ? 1 : 0,
        outOfStockCount: inventoryItem.quantity === 0 ? 1 : 0,
        averagePrice: inventoryItem.price || 0,
        stockUtilization: 0,
      };
    }

    const totalStock = variants.reduce((sum, v) => sum + v.quantity, 0);
    const totalValue = variants.reduce(
      (sum, v) => sum + v.quantity * v.price,
      0
    );
    const lowStockCount = variants.filter(
      v => v.quantity <= v.reorderLevel
    ).length;
    const outOfStockCount = variants.filter(v => v.quantity === 0).length;
    const averagePrice =
      variants.reduce((sum, v) => sum + v.price, 0) / variants.length;
    const maxPossibleStock = variants.reduce(
      (sum, v) => sum + v.reorderLevel * 3,
      0
    ); // Estimate
    const stockUtilization =
      maxPossibleStock > 0 ? (totalStock / maxPossibleStock) * 100 : 0;

    return {
      totalVariants: variants.length,
      totalStock,
      totalValue,
      lowStockCount,
      outOfStockCount,
      averagePrice,
      stockUtilization: Math.min(stockUtilization, 100),
    };
  }, [variants, inventoryItem]);

  const getStockStatusColor = () => {
    if (summary.outOfStockCount > 0) return 'text-red-600';
    if (summary.lowStockCount > 0) return 'text-orange-600';
    return 'text-green-600';
  };

  const getStockUtilizationColor = () => {
    if (summary.stockUtilization >= 80) return 'bg-green-500';
    if (summary.stockUtilization >= 50) return 'bg-yellow-500';
    return 'bg-red-500';
  };

  if (!inventoryItem.isVariant || variants.length === 0) {
    return (
      <Card className={className}>
        <CardContent className='p-4'>
          <div className='flex items-center gap-3'>
            <CubeIcon className='h-8 w-8 text-gray-400' />
            <div>
              <div className='font-medium text-gray-900'>Single Product</div>
              <div className='text-sm text-gray-600'>
                {formatNumber(inventoryItem.quantity)}{' '}
                {String(inventoryItem.measurementUnit) || 'units'}
                {' • '}
                {formatCurrency(inventoryItem.price || 0)}
              </div>
            </div>
          </div>
        </CardContent>
      </Card>
    );
  }

  return (
    <Card className={className}>
      <CardHeader className='pb-3'>
        <CardTitle className='flex items-center justify-between text-base'>
          <span className='flex items-center gap-2'>
            <TagIcon className='h-5 w-5 text-blue-500' />
            Product Variants
          </span>
          <Badge variant='secondary'>{summary.totalVariants}</Badge>
        </CardTitle>
      </CardHeader>
      <CardContent>
        <div className='space-y-4'>
          {/* Key Metrics */}
          <div className='grid grid-cols-2 gap-4'>
            <div className='space-y-1'>
              <div className='flex items-center gap-2'>
                <PackageIcon className={`h-4 w-4 ${getStockStatusColor()}`} />
                <span className='text-sm font-medium text-gray-600'>
                  Total Stock
                </span>
              </div>
              <div className={`text-lg font-bold ${getStockStatusColor()}`}>
                {formatNumber(summary.totalStock)}
                <span className='text-sm font-normal text-gray-500 ml-1'>
                  {String(inventoryItem.measurementUnit) || 'units'}
                </span>
              </div>
            </div>

            <div className='space-y-1'>
              <div className='flex items-center gap-2'>
                <CurrencyDollarIcon className='h-4 w-4 text-green-500' />
                <span className='text-sm font-medium text-gray-600'>
                  Total Value
                </span>
              </div>
              <div className='text-lg font-bold text-green-600'>
                {formatCurrency(summary.totalValue)}
              </div>
            </div>
          </div>

          {showDetails && (
            <>
              {/* Stock Utilization */}
              <div className='space-y-2'>
                <div className='flex justify-between items-center'>
                  <span className='text-sm font-medium text-gray-600'>
                    Stock Utilization
                  </span>
                  <span className='text-sm text-gray-500'>
                    {Math.round(summary.stockUtilization)}%
                  </span>
                </div>
                <Progress
                  value={summary.stockUtilization}
                  className='h-2'
                  style={
                    {
                      '--progress-background': getStockUtilizationColor(),
                    } as React.CSSProperties
                  }
                />
              </div>

              {/* Stock Status Indicators */}
              <div className='flex justify-between text-sm'>
                <div className='flex items-center gap-1'>
                  <div className='w-2 h-2 bg-green-500 rounded-full'></div>
                  <span className='text-gray-600'>
                    {summary.totalVariants -
                      summary.lowStockCount -
                      summary.outOfStockCount}{' '}
                    In Stock
                  </span>
                </div>
                {summary.lowStockCount > 0 && (
                  <div className='flex items-center gap-1'>
                    <div className='w-2 h-2 bg-orange-500 rounded-full'></div>
                    <span className='text-orange-600'>
                      {summary.lowStockCount} Low
                    </span>
                  </div>
                )}
                {summary.outOfStockCount > 0 && (
                  <div className='flex items-center gap-1'>
                    <div className='w-2 h-2 bg-red-500 rounded-full'></div>
                    <span className='text-red-600'>
                      {summary.outOfStockCount} Out
                    </span>
                  </div>
                )}
              </div>

              {/* Top Variants */}
              {variants.length > 0 && (
                <div className='space-y-2 pt-2 border-t'>
                  <div className='text-sm font-medium text-gray-600'>
                    Top Variants by Stock
                  </div>
                  <div className='space-y-1'>
                    {variants
                      .sort((a, b) => b.quantity - a.quantity)
                      .slice(0, 3)
                      .map((variant, index) => (
                        <div
                          key={variant._id || index}
                          className='flex justify-between items-center text-sm'
                        >
                          <span className='truncate flex-1 mr-2'>
                            {variant.name}
                          </span>
                          <div className='flex items-center gap-2'>
                            <span className='text-gray-600'>
                              {formatNumber(variant.quantity)}
                            </span>
                            <span className='font-medium text-green-600'>
                              {formatCurrency(variant.price)}
                            </span>
                          </div>
                        </div>
                      ))}
                    {variants.length > 3 && (
                      <div className='text-xs text-gray-500 text-center pt-1'>
                        +{variants.length - 3} more variants
                      </div>
                    )}
                  </div>
                </div>
              )}
            </>
          )}
        </div>
      </CardContent>
    </Card>
  );
};
